import { loadStripe } from '@stripe/stripe-js'
import type { PaymentRequest, Stripe } from '@stripe/stripe-js'
import { PLANS_DECORATIONS_MAP } from 'constants/plans-decorations-map'
import {
  PaymentSystem,
  PlatformOS,
  type IPaymentMethodsConfigProps,
} from 'constants/rootConstants'
import { getDefaultPlan } from 'helpers/getDefaultPlan'
import { getMobileOperatingSystem } from 'helpers/getMobileOperatingSystem'
import { getPlanDecoration } from 'helpers/getPlanDecoration'
import { getFunnelsConfig } from 'helpers/getWebFunnelsConfig'
import { OnboardingType } from 'models/lead.model'
import { useZustandLocalStore } from 'zustandGlobalLocalStore'
import { useZustandStore } from 'zustandGlobalStore'

export const WALLET_FALLBACK_AMOUNT_CENTS = 100

export type TWalletButtonTypes = {
  applePay: boolean
  googlePay: boolean
}

type TWalletPrefetchStatus = 'idle' | 'pending' | 'ready' | 'unavailable'

export type TWalletVariant = 'applepay' | 'googlepay'

export type TWalletCache = {
  paymentRequest: PaymentRequest
  buttonTypes: TWalletButtonTypes
  amountCents: number
  onboardingType: OnboardingType
  currency: string
}

let walletCache: TWalletCache | null = null
let inFlightPrefetch: Promise<void> | null = null
let inFlightKey: string | null = null

const getPrefetchCacheKey = (
  onboardingType: OnboardingType,
  amountCents: number,
  currency: string,
): string => `${onboardingType}:${amountCents}:${currency}`

const setWalletPrefetchStatus = (status: TWalletPrefetchStatus): void => {
  useZustandStore.setState({ walletPrefetchStatus: status })
}

export const setWalletUnavailable = (): void => {
  walletCache = null
  useZustandStore.setState({ isPaymentFlowsShown: false })
  setWalletPrefetchStatus('unavailable')
}

export const isFunnelsConfigReady = (): boolean =>
  !!useZustandStore.getState().growthbook

export const getCurrentWalletVariant = (): TWalletVariant =>
  getMobileOperatingSystem() === PlatformOS.IOS ? 'applepay' : 'googlepay'

export const isWalletMethodShown = (
  paymentMethodsConfig: IPaymentMethodsConfigProps,
  walletVariant: TWalletVariant = getCurrentWalletVariant(),
): boolean =>
  walletVariant === 'applepay'
    ? paymentMethodsConfig.applePayMethodShown
    : paymentMethodsConfig.googlePayMethodShown

export const getShownWalletButtonTypes = (
  buttonTypes: TWalletButtonTypes,
  paymentMethodsConfig: IPaymentMethodsConfigProps,
  walletVariant: TWalletVariant = getCurrentWalletVariant(),
): TWalletButtonTypes => ({
  applePay:
    walletVariant === 'applepay' &&
    buttonTypes.applePay &&
    paymentMethodsConfig.applePayMethodShown,
  googlePay:
    walletVariant === 'googlepay' &&
    buttonTypes.googlePay &&
    paymentMethodsConfig.googlePayMethodShown,
})

export const hasShownWalletButtonTypes = (
  buttonTypes: TWalletButtonTypes,
): boolean => buttonTypes.applePay || buttonTypes.googlePay

const getWalletAmountCents = (
  onboardingType: OnboardingType,
  planName?: string,
): number => {
  const state = useZustandStore.getState()
  const cohort = state.lead?.cohort || state.initialCohort
  const currency = state.cohortLeadCurrency
  const plans = state.cohortLeadPlans?.[onboardingType] || []
  const decorations =
    PLANS_DECORATIONS_MAP[cohort]?.[currency]?.[onboardingType] || []

  const selectedPlan = useZustandLocalStore.getState().selectedLeadPlan
  const plan =
    (planName && plans.find((p) => p.name === planName)) ||
    (selectedPlan?.onboardingType === onboardingType ? selectedPlan : null) ||
    getDefaultPlan(plans, decorations)

  if (!plan) return WALLET_FALLBACK_AMOUNT_CENTS

  const decoration = getPlanDecoration({
    cohort,
    currency,
    onboardingType,
    planName: plan.name,
  })

  const dueToday =
    decoration?.analyticsTrialPrice ?? decoration?.analyticsPrice ?? 0
  const amount = Math.round(dueToday * 100)

  return amount > 0 ? amount : WALLET_FALLBACK_AMOUNT_CENTS
}

const ensureStripeReady = async (): Promise<Stripe | null> => {
  const state = useZustandStore.getState()
  const stripePublicKey = state.lead?.config?.payment?.find(
    (payment) => payment.type === PaymentSystem.STRIPE,
  )?.stripePublicKey

  if (!stripePublicKey) return null

  if (!state.stripePromise) {
    const stripePromise = loadStripe(stripePublicKey)
    useZustandStore.setState({
      stripePromise,
      isStripeLoaded: true,
    })
  }

  const stripe = await useZustandStore.getState().stripePromise
  return stripe ?? null
}

const createPaymentRequest = (
  stripe: Stripe,
  currency: string,
  amountCents: number,
): PaymentRequest =>
  stripe.paymentRequest({
    currency,
    country: 'GB',
    requestPayerEmail: true,
    requestPayerName: true,
    total: {
      label: 'Intellio IQ Subscription',
      amount: amountCents,
    },
  })

export const setDefaultSelectedPlan = (
  onboardingType: OnboardingType,
): void => {
  const state = useZustandStore.getState()
  const plans = state.cohortLeadPlans?.[onboardingType] || []
  const cohort = state.lead?.cohort || state.initialCohort
  const currency = state.cohortLeadCurrency
  const decorations =
    PLANS_DECORATIONS_MAP[cohort]?.[currency]?.[onboardingType] || []

  const defaultPlan = getDefaultPlan(plans, decorations)
  if (defaultPlan) {
    useZustandLocalStore.setState({ selectedLeadPlan: defaultPlan })
  }
}

export const getCachedWalletPayment = (
  onboardingType: OnboardingType,
  amountCents: number,
  currency: string,
): TWalletCache | null => {
  if (
    !walletCache ||
    walletCache.onboardingType !== onboardingType ||
    walletCache.amountCents !== amountCents ||
    walletCache.currency !== currency
  ) {
    return null
  }

  return walletCache
}

export const prefetchWalletPayment = (
  onboardingType: OnboardingType,
): Promise<void> => {
  if (!isFunnelsConfigReady()) {
    return Promise.resolve()
  }

  const paymentMethodsConfig = getFunnelsConfig()

  if (!isWalletMethodShown(paymentMethodsConfig)) {
    setWalletUnavailable()
    return Promise.resolve()
  }

  const amountCents = getWalletAmountCents(onboardingType)
  const currency =
    useZustandStore.getState().lead?.config?.cohort?.currency?.toLowerCase() ||
    'usd'
  const cacheKey = getPrefetchCacheKey(onboardingType, amountCents, currency)

  if (
    walletCache &&
    walletCache.onboardingType === onboardingType &&
    walletCache.amountCents === amountCents &&
    walletCache.currency === currency
  ) {
    useZustandStore.setState({ isPaymentFlowsShown: true })
    setWalletPrefetchStatus('ready')
    return Promise.resolve()
  }

  if (inFlightPrefetch && inFlightKey === cacheKey) {
    return inFlightPrefetch
  }

  inFlightKey = cacheKey
  setWalletPrefetchStatus('pending')

  inFlightPrefetch = (async () => {
    try {
      const stripe = await ensureStripeReady()
      if (!stripe) {
        setWalletUnavailable()
        return
      }

      const pr = createPaymentRequest(stripe, currency, amountCents)
      const result = await pr.canMakePayment()

      if (result) {
        const buttonTypes = getShownWalletButtonTypes(
          result as TWalletButtonTypes,
          paymentMethodsConfig,
        )

        if (!hasShownWalletButtonTypes(buttonTypes)) {
          setWalletUnavailable()
          return
        }

        walletCache = {
          paymentRequest: pr,
          buttonTypes,
          amountCents,
          onboardingType,
          currency,
        }
        useZustandStore.setState({ isPaymentFlowsShown: true })
        setWalletPrefetchStatus('ready')
      } else {
        setWalletUnavailable()
      }
    } catch {
      setWalletUnavailable()
    } finally {
      inFlightPrefetch = null
      inFlightKey = null
    }
  })()

  return inFlightPrefetch
}

export const getWalletPrefetchPromise = (
  onboardingType: OnboardingType = OnboardingType.PURCHASE,
): Promise<void> => {
  if (!isFunnelsConfigReady()) {
    return Promise.resolve()
  }

  const paymentMethodsConfig = getFunnelsConfig()

  if (!isWalletMethodShown(paymentMethodsConfig)) {
    setWalletUnavailable()
    return Promise.resolve()
  }

  const status = useZustandStore.getState().walletPrefetchStatus

  if (status === 'ready' || status === 'unavailable') {
    return Promise.resolve()
  }

  if (inFlightPrefetch) {
    return inFlightPrefetch
  }

  return prefetchWalletPayment(onboardingType)
}
